import { ContextConsumer } from '@lit/context';
import { localized, msg } from '@lit/localize';
import { t } from '../../../../services/runtime-i18n';
import { LitElement, TemplateResult, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { repeat } from 'lit/directives/repeat.js';
import urlJoin from 'url-join';
import { ENTITLE_BASE_PATH, EPML_BASE_PATH, PRA_BASE_PATH, RS_BASE_PATH } from '../../../../constants';
import { menuDataContext } from '../../../../context/menu-data';
import { DirtyFormController } from '../../../../controllers/dirty-form-controller';
import {
  APIMenuItem,
  APITagItem,
  ENTITLE_SLUG,
  EPML_SLUG,
  INSIGHTS_SLUG,
  PASSWORD_SAFE_SLUG,
  PMC_SLUG,
  PRIVILEGED_REMOTE_ACCESS_SLUG,
  ProductMenuItem,
  REMOTE_SUPPORT_SLUG,
} from '../../../../types';
import '../../../tag/tag';
import styles from '../menu.styles.scss';
import reportingStyles from './reporting-menu.styles.scss';
export const REPORTING_MENU_TAG = 'platform-reporting-menu' as const;

/**
 * Reporting menu.
 */
@customElement(REPORTING_MENU_TAG)
@localized()
export class ReportingProductMenu extends LitElement {
  static override styles = [styles, reportingStyles];

  @property({ type: Array })
  productMenuItems!: ProductMenuItem[];

  dirtyFormController = new DirtyFormController(this);
  _menuData = new ContextConsumer(this, { context: menuDataContext, subscribe: true });

  override render() {
    return html` ${repeat(this.productMenuItems, (product) =>
      choose(
        product.slug,
        [
          [INSIGHTS_SLUG, () => this.renderInsightsMenu()],
          [EPML_SLUG, () => this.renderEPMLMenu()],
          [PMC_SLUG, () => this.renderPMCMenu()],
          [PASSWORD_SAFE_SLUG, () => this.renderPasswordsafeMenu()],
          [REMOTE_SUPPORT_SLUG, () => this.renderRSMenu()],
          [PRIVILEGED_REMOTE_ACCESS_SLUG, () => this.renderPRAMenu()],
          [ENTITLE_SLUG, () => this.renderEntitleMenu()],
        ],
        () => html``,
      ),
    )}`;
  }

  private renderInsightsMenu(): TemplateResult<1> | typeof nothing {
    const hasReporting = this._menuData.value?.[INSIGHTS_SLUG]?.features
      ?.flatMap((f) => f.items)
      .some((i) => i.id === 'insights.menu.reporting');
    if (!hasReporting) {
      return nothing;
    }
    return html`<h3><platform-product-icon productSlug=${INSIGHTS_SLUG}></platform-product-icon>Insights</h3>
      <slot name="reporting-insights-menu"></slot>`;
  }

  private renderEPMLMenu(): TemplateResult<1> | typeof nothing {
    const epmlMenu = this._menuData.value?.[EPML_SLUG];
    if (epmlMenu == undefined || epmlMenu.features == undefined) {
      return nothing;
    }
    const auditMenu = epmlMenu.features.find((f) => f.id === 'epml.menu.audit');
    if (auditMenu == undefined || auditMenu.items.length === 0) {
      return nothing;
    }
    const renderMenuItem = (item: APIMenuItem) =>
      html`<li>
        <a href=${urlJoin(EPML_BASE_PATH, item.link)}> ${t(item.id, item.name)}${this.renderTags(item.tags)} </a>
      </li>`;

    return html`<h3><platform-product-icon productSlug=${EPML_SLUG}></platform-product-icon>EPM - Linux</h3>
      <menu>${auditMenu.items.map(renderMenuItem)}</menu>`;
  }

  private renderPasswordsafeMenu(): TemplateResult<1> | typeof nothing {
    if (this._menuData.value?.passwordsafe == undefined || this._menuData.value?.passwordsafe.features == undefined) {
      return nothing;
    }
    const AANDRMenuItem = this._menuData.value.passwordsafe.features.find(
      (i) => i.id === 'sitemenu.analyticsandreportingtrident',
    );
    if (AANDRMenuItem == undefined) {
      return nothing;
    }
    return html`<h3><platform-product-icon productSlug=${PASSWORD_SAFE_SLUG}></platform-product-icon>Password Safe</h3>
      <menu>
        <li>
          <a href=${AANDRMenuItem.link || ''}> ${t(AANDRMenuItem.id, AANDRMenuItem.name ?? '')} </a>
        </li>
      </menu>`;
  }

  private renderRSMenu(): TemplateResult<1> | typeof nothing {
    if (this._menuData.value?.rs == undefined || this._menuData.value?.rs.features == undefined) {
      return nothing;
    }
    const reportingMenuItem = this._menuData.value.rs.features[0].items.find((i) => i.id === 'reporting');
    if (reportingMenuItem == undefined) {
      return nothing;
    }
    return html`<h3>
        <platform-product-icon productSlug=${REMOTE_SUPPORT_SLUG}></platform-product-icon>Remote Support
      </h3>
      <menu>
        <li>
          <a href=${urlJoin(RS_BASE_PATH, reportingMenuItem.link)}
            >${t(reportingMenuItem.id, reportingMenuItem.name)}</a
          >
        </li>
      </menu>`;
  }

  private renderPRAMenu(): TemplateResult<1> | typeof nothing {
    if (
      this._menuData.value?.['privileged-remote-access'] == undefined ||
      this._menuData.value?.['privileged-remote-access'].features == undefined
    ) {
      return nothing;
    }
    const reportingMenuItem = this._menuData.value['privileged-remote-access'].features[0].items.find(
      (i) => i.id === 'reporting',
    );
    if (reportingMenuItem == undefined) {
      return nothing;
    }
    // Keep the name in `${'...'}`. As bare text this line exceeds printWidth,
    // so Prettier reflows it onto two source lines, which puts a newline inside
    // the rendered text node. The other headers are short enough to stay put.
    return html`<h3>
        <platform-product-icon productSlug=${PRIVILEGED_REMOTE_ACCESS_SLUG}></platform-product-icon
        >${'Privileged Remote Access'}
      </h3>
      <menu>
        <li>
          <a href=${urlJoin(PRA_BASE_PATH, reportingMenuItem.link)}
            >${t(reportingMenuItem.id, reportingMenuItem.name)}</a
          >
        </li>
      </menu>`;
  }

  private renderPMCMenu(): TemplateResult<1> | typeof nothing {
    const pmcMenu = this._menuData.value?.[PMC_SLUG];
    if (pmcMenu == undefined || pmcMenu.features == undefined) {
      return nothing;
    }
    const auditMenu = pmcMenu.features.find((f) => f.id === 'pmc.audit');
    if (auditMenu == undefined || auditMenu.items.length === 0) {
      return nothing;
    }
    const renderMenuItem = (item: APIMenuItem) =>
      html`<li>
        <a href=${item.link}> ${t(item.id, item.name)}${this.renderTags(item.tags)} </a>
      </li>`;

    return html`<h3><platform-product-icon productSlug=${PMC_SLUG}></platform-product-icon>EPM - Windows and Mac</h3>
      <menu>${auditMenu.items.map(renderMenuItem)}</menu>`;
  }

  private renderEntitleMenu(): TemplateResult<1> | typeof nothing {
    const entitleMenu = this._menuData.value?.[ENTITLE_SLUG];
    if (entitleMenu == undefined || entitleMenu.features == undefined) {
      return nothing;
    }
    const flatMenuItems = entitleMenu.features.flatMap((f) => f.items);
    const auditLogMenuItem = flatMenuItems.find((i) => i.id === 'entitle.menu.auditLogs');
    const requestHistoryMenuItem = flatMenuItems.find((i) => i.id === 'entitle.menu.requestsLog');

    if (auditLogMenuItem == undefined && requestHistoryMenuItem == undefined) {
      return nothing;
    }

    return html`<h3><platform-product-icon productSlug=${ENTITLE_SLUG}></platform-product-icon>Entitle</h3>
      <menu>
        ${choose(auditLogMenuItem != undefined, [
          [
            true,
            () =>
              html`<li>
                <a href=${urlJoin(ENTITLE_BASE_PATH, auditLogMenuItem?.link || '')}
                  >${t(auditLogMenuItem?.id, auditLogMenuItem?.name ?? '')}</a
                >
              </li>`,
          ],
        ])}
        ${choose(requestHistoryMenuItem != undefined, [
          [
            true,
            () =>
              html`<li>
                <a href=${urlJoin(ENTITLE_BASE_PATH, requestHistoryMenuItem?.link || '')}
                  >${requestHistoryMenuItem?.name}</a
                >
              </li>`,
          ],
        ])}
      </menu>`;
  }

  private renderTags(tags?: APITagItem[]): TemplateResult<1> | typeof nothing {
    if (tags == undefined) {
      return nothing;
    }
    return html`${tags.map(
      (tag) =>
        html`${choose(
          tag.name,
          [
            ['new', () => html`<platform-tag variant="new">${msg('New')}</platform-tag>`],
            ['preview', () => html`<platform-tag variant="preview">${msg('Preview')}</platform-tag>`],
            ['early access', () => html`<platform-tag variant="early-access">${msg('Early Access')}</platform-tag>`],
          ],
          () => html``,
        )}`,
    )}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [REPORTING_MENU_TAG]: ReportingProductMenu;
  }
}
